import { useEffect, useState, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { useResponsive } from '../responsive';
import { BottomNavBar } from './BottomNavBar';
import './Sidebar.css';

const EXPANDED_GROUPS_STORAGE_KEY = 'sidebar-expanded-groups';

function loadExpandedGroups(): Record<string, boolean> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(EXPANDED_GROUPS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveExpandedGroups(state: Record<string, boolean>): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(EXPANDED_GROUPS_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore quota / privacy-mode errors
  }
}

export interface NavItem {
  /** Route path. Omit for a group header that only labels its children. */
  path?: string;
  label: string;
  icon?: string;
  end?: boolean;
  children?: NavItem[];
  bottom?: boolean;
  /** Mobile only: always live in the bottom bar's "More" overflow menu rather
   *  than as a primary tab. Ignored on desktop (the full sidebar shows every
   *  item). Use for secondary destinations that shouldn't claim a scarce tab
   *  slot — e.g. the student app's You / Bookmarks / Insights. */
  overflow?: boolean;
  badge?: number;
  /** When true, the link is rendered greyed-out and clicking it pops
   *  `disabledMessage` instead of navigating. */
  disabled?: boolean;
  /** Message shown via window.alert when a disabled item is clicked. */
  disabledMessage?: string;
}

export interface SidebarProps {
  subtitle: string;
  userName?: string;
  navItems: NavItem[];
  onLogout?: () => void;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  /** Custom content rendered between the welcome row and the nav (e.g. a tenant filter). */
  topSlot?: ReactNode;
  /** Mobile only: when true, render no bottom nav bar. Used so the student app
   *  shows the bar only on L1 tab pages. Desktop sidebar is unaffected. */
  hideOnMobile?: boolean;
  /** T3c tenant branding: a logo shown in place of the "BuzzBrain" wordmark.
   *  Absent on a branded tenant → no mark at all (see `brandName`); a tenant
   *  that hasn't uploaded one gets nothing rather than a stand-in. */
  logoUrl?: string;
  /** The tenant's name, shown under the logo. Its presence is what marks the
   *  sidebar as tenant-branded: set → the tenant identifies itself (logo
   *  and/or name); absent → the default BuzzBrain wordmark. */
  brandName?: string;
}

export function Sidebar(props: SidebarProps) {
  const { subtitle, userName, navItems, onLogout, collapsible = true, defaultCollapsed = false, topSlot, logoUrl, brandName } = props;
  const { isMobile } = useResponsive();
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(loadExpandedGroups);

  useEffect(() => {
    saveExpandedGroups(expandedGroups);
  }, [expandedGroups]);

  const isGroupExpanded = (label: string): boolean => expandedGroups[label] !== false; // default expanded

  const toggleGroup = (label: string): void => {
    setExpandedGroups(prev => ({ ...prev, [label]: !isGroupExpanded(label) }));
  };

  if (isMobile) {
    return props.hideOnMobile ? null : <BottomNavBar {...props} />;
  }

  const topItems = navItems.filter(item => !item.bottom);
  const bottomItems = navItems.filter(item => item.bottom);

  const renderNavItem = (item: NavItem) => {
    const key = item.path ?? `group:${item.label}`;
    const isGroupHeader = !item.path;
    const hasChildren = !!item.children?.length;
    const expanded = isGroupExpanded(item.label);

    // When collapsed, group headers without a route would just render as a
    // disembodied icon — confusing. Render only the icon, and keep children
    // inline (they still each have a route).
    const headerContent = isGroupHeader ? (
      <div
        className={`sidebar-nav-item sidebar-nav-group ${collapsed ? 'sidebar-nav-group--collapsed' : ''} ${hasChildren && !collapsed ? 'sidebar-nav-group--clickable' : ''}`}
        title={item.label}
        onClick={hasChildren && !collapsed ? () => toggleGroup(item.label) : undefined}
        role={hasChildren && !collapsed ? 'button' : undefined}
        tabIndex={hasChildren && !collapsed ? 0 : undefined}
        onKeyDown={hasChildren && !collapsed ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleGroup(item.label); }
        } : undefined}
      >
        {collapsed
          ? <span className="sidebar-nav-icon">{item.icon ?? item.label.charAt(0)}</span>
          : (
            <>
              <span className="sidebar-nav-group-label">{item.label}</span>
              {hasChildren && (
                <span className="sidebar-nav-group-chevron" aria-hidden="true">
                  {expanded ? '▾' : '▸'}
                </span>
              )}
            </>
          )}
      </div>
    ) : (
      <NavLink
        to={item.path!}
        end={item.end}
        onClick={item.disabled ? (e) => {
          e.preventDefault();
          if (item.disabledMessage) window.alert(item.disabledMessage);
        } : undefined}
        className={({ isActive }: { isActive: boolean }) =>
          `sidebar-nav-item ${isActive && !item.disabled ? 'active' : ''} ${item.disabled ? 'sidebar-nav-item--disabled' : ''}`
        }
        title={item.disabled ? (item.disabledMessage ?? item.label) : item.label}
      >
        {collapsed && item.icon ? (
          <>
            <span className="sidebar-nav-icon">{item.icon}</span>
            {item.badge ? <span className="sidebar-nav-badge">{item.badge}</span> : null}
          </>
        ) : collapsed ? (
          <>
            <span className="sidebar-nav-icon">{item.label.charAt(0)}</span>
            {item.badge ? <span className="sidebar-nav-badge">{item.badge}</span> : null}
          </>
        ) : (
          <>
            {item.label}
            {item.badge ? <span className="sidebar-nav-badge">{item.badge}</span> : null}
          </>
        )}
      </NavLink>
    );

    const showChildren = !collapsed && hasChildren && (!isGroupHeader || expanded);
    return (
      <div key={key}>
        {headerContent}
        {showChildren && item.children!.map((child) => (
          <NavLink
            key={child.path}
            to={child.path!}
            end={child.end}
            onClick={child.disabled ? (e) => {
              e.preventDefault();
              if (child.disabledMessage) window.alert(child.disabledMessage);
            } : undefined}
            className={({ isActive }: { isActive: boolean }) =>
              `sidebar-nav-item sidebar-nav-child ${isActive && !child.disabled ? 'active' : ''} ${child.disabled ? 'sidebar-nav-item--disabled' : ''}`
            }
            title={child.disabled ? (child.disabledMessage ?? child.label) : child.label}
          >
            {child.label}
            {child.badge ? <span className="sidebar-nav-badge">{child.badge}</span> : null}
          </NavLink>
        ))}
      </div>
    );
  };

  // Sidebar tint by environment so it's obvious at a glance which target you're on:
  //   MODE=development → local dev server (blue tint)
  //   MODE=dev         → deployed dev env (amber tint)
  //   MODE=production  → prod (no tint)
  const mode = import.meta.env.MODE;
  const envClass = mode === 'dev' ? 'sidebar--dev-env' : mode === 'development' ? 'sidebar--local-env' : '';

  return (
    <aside className={`sidebar ${collapsed ? 'sidebar--collapsed' : ''} ${envClass}`}>
      <div className="sidebar-top">
        <div className="sidebar-header">
          {!collapsed && (
            <div className="sidebar-logo">
              {/* A branded tenant shows its own mark: the logo if it uploaded
                  one, its name underneath either way. No logo means no image —
                  a stand-in mark would misattribute the tenant. The BuzzBrain
                  wordmark is for unbranded (public/platform) callers only. */}
              {logoUrl && <img src={logoUrl} alt={brandName ?? subtitle} className="sidebar-logo-img" />}
              {brandName
                ? <span className="sidebar-brand-name">{brandName}</span>
                : !logoUrl && <h1>BuzzBrain</h1>}
              <span>{subtitle}</span>
            </div>
          )}
          {collapsible && (
            <button
              className="sidebar-collapse"
              onClick={() => setCollapsed(!collapsed)}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? '\u2630' : '\u2039'}
            </button>
          )}
        </div>
      </div>

      {userName && !collapsed && (
        <div className="sidebar-welcome">
          Welcome, {userName}
        </div>
      )}

      {topSlot && !collapsed && (
        <div className="sidebar-top-slot">{topSlot}</div>
      )}

      <nav className="sidebar-nav">
        {topItems.map(renderNavItem)}
      </nav>

      {bottomItems.length > 0 && (
        <div className="sidebar-bottom-nav">
          {bottomItems.map(renderNavItem)}
        </div>
      )}

      {onLogout && (
        <div className="sidebar-footer">
          <button onClick={onLogout} className="sidebar-logout" title="Logout">
            {collapsed ? '\u2192' : 'Logout'}
          </button>
        </div>
      )}
    </aside>
  );
}
